import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useParams, useNavigate } from "react-router-dom";
const secretKey = process.env.REACT_APP_SECRET_KEY || "default-fallback-key";
const API_URL = process.env.REACT_APP_API_URL;
const API_FILE = process.env.REACT_APP_FILE_URL;
const URL = process.env.PUBLIC_URL;

type CardProps = {
    drawings: any[];
    reload: () => void;
    scrollIndex?: number | null;
    setScrollIndex: React.Dispatch<React.SetStateAction<number | null>>;
};

export default function Card({ drawings, reload, scrollIndex, setScrollIndex }: CardProps) {
    //load index
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    // textarea note
    const [note, setNote] = useState("");
    //drag chuột
    const [dragOver, setDragOver] = useState(false);
    //vẽ
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    type Rect = {
        x: number; y: number; w: number; h: number; note?: string;
        detailId?: number | null;
        drawingId?: number | null;
    };
    const [paths, setPaths] = useState<Rect[]>([]);
    const [startPos, setStartPos] = useState<{ x: number, y: number } | null>(null);
    const [currentRect, setCurrentRect] = useState<{ x: number, y: number, w: number, h: number } | null>(null);
    type DragState =
        | { index: number; type: "move"; offsetX: number; offsetY: number }
        | { index: number; type: "resize"; corner: "tl" | "tr" | "bl" | "br" }
        | null;
    const [dragState, setDragState] = useState<DragState>(null);
    const resizeHandleSize = 8;
    // set vị trí group note
    const [activeRectIndex, setActiveRectIndex] = useState<number | null>(null);
    // scroll img cuối
    const lastImageRef = useRef<HTMLDivElement | null>(null);
    //input ẩn upload file
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // url mặc định
    const location = useLocation();
    const isDetailPage = /^\/detail\/[^/]+$/.test(location.pathname);
    //scroll tới hình vẽ
    const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
    // id
    const { id } = useParams();
    // let id = "";

    const navigate = useNavigate();

    // dịch id bị mã hóa
    // if (id) {
    //     try {
    //         const bytes = CryptoJS.AES.decrypt(decodeURIComponent(id), secretKey);
    //         id = bytes.toString(CryptoJS.enc.Utf8);
    //     } catch (e) {
    //         console.error("Decrypt error:", e);
    //     }
    // }

    // api index edit page
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!id) return; // ✅ Kiểm tra id tồn tại trước

                const decodedId = decodeURIComponent(id);

                const res = await fetch(`${API_URL}/edit/${decodedId}`, {
                    method: "GET",
                    credentials: "include",
                });

                if (!res.ok) throw new Error(`Error ${res.status}`);

                const result = await res.json();
                setData(result.url);
                console.log("result.drawings:", result.drawings);

                if (result.success && result.drawings) {
                    const allPaths: Rect[] = [];

                    result.drawings.forEach((d: any) => {
                        let coords = [];
                        try {
                            coords = Array.isArray(d.coordinates)
                                ? d.coordinates
                                : JSON.parse(d.coordinates) || [];
                        } catch {
                            coords = [];
                        }

                        const details = d.details || [];

                        coords.forEach((c: any, idx: number) => {
                            allPaths.push({
                                x: Number(c.x),
                                y: Number(c.y),
                                w: Number(c.w),
                                h: Number(c.h),
                                note: details[idx]?.note || "",
                                detailId: details[idx]?.id || null,
                                drawingId: d.id,
                            });
                        });
                    });

                    setPaths(allPaths);
                }
            } catch (err) {
                console.error("API error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);


    // click upload đẩy dữ liệu vào input ẩn
    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // api upload file
    const handleFileUpload = async (file: File) => {
        const formData = new FormData();
        formData.append("file_upload", file);

        try {
            if (!id) return; // ✅ Kiểm tra id tồn tại trước

            const decodedId = decodeURIComponent(id);

            const res = await fetch(`${API_URL}/upload-card/${decodedId}`, {
                method: "POST",
                body: formData,
                credentials: "include",
            });

            const dataRes = await res.json();

            if (dataRes.success) {
                localStorage.setItem("scrollToLastImage", "true");

                // Reload lại trang
                window.location.reload();
                // const encryptedId = CryptoJS.AES.encrypt(id, secretKey).toString();
                // const encodedId = encodeURIComponent(encryptedId);

                // window.location.href = `${URL}/edit/${encodedId}`;
            }
        } catch (err) {
            console.error("Upload error:", err);
        }
    };

    // scroll tới ảnh cuối
    useEffect(() => {
        if (localStorage.getItem("scrollToLastImage") === "true") {
            const timer = setTimeout(() => {
                lastImageRef.current?.scrollIntoView({ behavior: "smooth" });
                localStorage.removeItem("scrollToLastImage");
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [data?.uploaded_images]);

    // click upload 
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        handleFileUpload(e.target.files[0]);
    };

    // drop upload
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileUpload(e.dataTransfer.files[0]);
            e.dataTransfer.clearData();
        }
    };

    // vẽ canva
    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.lineWidth = 2;
        ctx.strokeStyle = "red";
    }, [containerRef.current?.clientWidth, containerRef.current?.clientHeight]);

    const getCornerUnderMouse = (r: Rect, x: number, y: number): "tl" | "tr" | "bl" | "br" | null => {
        const corners = {
            tl: { x: r.x, y: r.y },
            tr: { x: r.x + r.w, y: r.y },
            bl: { x: r.x, y: r.y + r.h },
            br: { x: r.x + r.w, y: r.y + r.h },
        };
        for (let key in corners) {
            const c = corners[key as keyof typeof corners];
            if (Math.abs(x - c.x) < resizeHandleSize && Math.abs(y - c.y) < resizeHandleSize) return key as any;
        }
        return null;
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDetailPage) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rectCanvas = canvas.getBoundingClientRect();
        const x = e.clientX - rectCanvas.left;
        const y = e.clientY - rectCanvas.top;

        // Kiểm tra resize góc trước
        for (let i = paths.length - 1; i >= 0; i--) {
            const r = paths[i];
            const corner = getCornerUnderMouse(r, x, y);
            if (corner) {
                setDragState({ index: i, type: "resize", corner });
                setActiveRectIndex(i);
                setNote(r.note || "");
                return;
            }
        }

        // Kiểm tra move rectangle
        for (let i = paths.length - 1; i >= 0; i--) {
            const r = paths[i];
            if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
                setDragState({ index: i, type: "move", offsetX: x - r.x, offsetY: y - r.y });
                setActiveRectIndex(i);
                setNote(r.note || "");
                return;
            }
        }

        // Nếu không chọn rectangle => vẽ mới
        setStartPos({ x, y });
        setCurrentRect({ x, y, w: 0, h: 0 });
        setActiveRectIndex(null);
        setNote("");
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDetailPage) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rectCanvas = canvas.getBoundingClientRect();
        const x = e.clientX - rectCanvas.left;
        const y = e.clientY - rectCanvas.top;

        let cursorStyle = "default";
        const updatedPaths = [...paths];

        // Nếu đang kéo (move hoặc resize)
        if (dragState) {
            const index = dragState.index;
            const r = updatedPaths[index];

            if (dragState.type === "move") {
                r.x = x - (dragState.offsetX ?? 0);
                r.y = y - (dragState.offsetY ?? 0);
                cursorStyle = "move";
            } else if (dragState.type === "resize") {
                switch (dragState.corner) {
                    case "tl":
                        r.w += r.x - x;
                        r.h += r.y - y;
                        r.x = x;
                        r.y = y;
                        cursorStyle = "nwse-resize";
                        break;
                    case "tr":
                        r.w = x - r.x;
                        r.h += r.y - y;
                        r.y = y;
                        cursorStyle = "nesw-resize";
                        break;
                    case "bl":
                        r.w += r.x - x;
                        r.h = y - r.y;
                        r.x = x;
                        cursorStyle = "nesw-resize";
                        break;
                    case "br":
                        r.w = x - r.x;
                        r.h = y - r.y;
                        cursorStyle = "nwse-resize";
                        break;
                }
            }

            setPaths(updatedPaths);
        }
        // Nếu đang vẽ rectangle mới
        else if (startPos) {
            const w = x - startPos.x;
            const h = y - startPos.y;
            setCurrentRect({ x: startPos.x, y: startPos.y, w, h });
        }

        // Kiểm tra hover để thay đổi cursor
        if (!dragState) {
            for (let i = paths.length - 1; i >= 0; i--) {
                const r = paths[i];
                const corner = getCornerUnderMouse(r, x, y);
                if (corner) {
                    cursorStyle = corner === "tl" || corner === "br" ? "nwse-resize" : "nesw-resize";
                    break;
                }
                if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
                    cursorStyle = "move";
                    break;
                }
            }
        }

        canvas.style.cursor = cursorStyle;

        // --- VẼ CANVAS ---
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Vẽ các hình đã lưu
        updatedPaths.forEach((r) => {
            ctx.fillStyle = "rgba(255,0,0,0.2)"; // màu nền
            ctx.strokeStyle = "red"; // màu viền
            ctx.fillRect(r.x, r.y, r.w, r.h);
            ctx.strokeRect(r.x, r.y, r.w, r.h);
        });

        // Vẽ hình đang vẽ (currentRect)
        if (currentRect) {
            ctx.fillStyle = "rgba(0,0,255,0.2)";
            ctx.strokeStyle = "blue";
            ctx.fillRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
            ctx.strokeRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
        }
    };

    // Số thứ tự
    // function drawRectWithNumber(
    //     ctx: CanvasRenderingContext2D,
    //     r: { x: number; y: number; w: number; h: number },
    //     number: number,
    //     color: string,
    //     fillColor: string
    // ) {
    //     const padding = 4;
    //     const circleRadius = 12;

    //     // Vẽ nền chữ nhật
    //     ctx.fillStyle = fillColor;
    //     ctx.fillRect(r.x, r.y, r.w, r.h);

    //     // Vẽ viền
    //     ctx.strokeStyle = color;
    //     ctx.strokeRect(r.x, r.y, r.w, r.h);

    //     // Vẽ vòng tròn số
    //     const cx = r.x + r.w - circleRadius - padding;
    //     const cy = r.y + circleRadius + padding;

    //     ctx.fillStyle = color;
    //     ctx.beginPath();
    //     ctx.arc(cx, cy, circleRadius, 0, 2 * Math.PI);
    //     ctx.fill();

    //     // Vẽ số
    //     ctx.fillStyle = "#fff";
    //     ctx.font = "bold 14px sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.textBaseline = "middle";
    //     ctx.fillText(number.toString(), cx, cy);
    // }

    const handleMouseUp = () => {
        if (isDetailPage) return;
        if (currentRect) {
            const absW = Math.abs(currentRect.w);
            const absH = Math.abs(currentRect.h);

            if (absW > 10 && absH > 10) {
                setPaths(prev => {
                    const newPaths = [...prev, { ...currentRect }];
                    setActiveRectIndex(newPaths.length - 1); // chọn hình mới vẽ
                    return newPaths;
                });
            }
            setCurrentRect(null);
            setStartPos(null);
            // setActiveRectIndex(paths.length);
        }
        setDragState(null);
    };

    const saveButtonPos = activeRectIndex !== null && paths[activeRectIndex]
        ? {
            top: paths[activeRectIndex].y + paths[activeRectIndex].h + 5,
            left: paths[activeRectIndex].x + paths[activeRectIndex].w + 5,
        }
        : null;

    // api save
    const saveDrawing = async () => {
        if (activeRectIndex === null) return;

        const target = paths[activeRectIndex];

        try {
            if (!id) return; // ✅ Kiểm tra id tồn tại trước

            const decodedId = decodeURIComponent(id);

            const res = await fetch(`${API_URL}/save-drawing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // body: JSON.stringify({
                //     url_id: id,
                //     // detail_id: target.detailId,
                //     note: note
                // }),

                body: JSON.stringify({ url_id: decodedId, coordinates: paths, note: note, detail_id: target.detailId, }),
                credentials: "include",
            });

            const data = await res.json();
            if (data.success) {

                setPaths(prev =>
                    prev.map((p, i) =>
                        i === activeRectIndex
                            ? { ...p, note, detailId: data.detail_id } // <- cập nhật id mới
                            : p
                    )
                );
                setNote("");
                setActiveRectIndex(null);
                reload();
            }
        } catch (err) {
            console.error(err);
        }
    };

    // api update note
    const updateDrawing = async () => {
        if (activeRectIndex === null) return;

        const target = paths[activeRectIndex];
        if (!target.detailId) {
            console.warn("Không có detailId, không thể cập nhật.");
            return;
        }

        try {
            // ⚙️ Nếu detailId được mã hóa (string)
            // const decodedId = decodeURIComponent(String(target.detailId));
            // console.log(decodedId)
            const res = await fetch(`${API_URL}/update-drawing/${target.detailId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ note }),
                credentials: "include",
            });

            const data = await res.json();

            if (data.success) {
                // ✅ Cập nhật note trong local state
                setPaths(prev =>
                    prev.map((p, i) =>
                        i === activeRectIndex
                            ? { ...p, note, detailId: data.detail_id } // <- cập nhật id mới
                            : p
                    )
                );
                setNote("");
                setActiveRectIndex(null);
                reload(); // reload lại dữ liệu
            } else {
                console.error("❌ Cập nhật thất bại:", data.message);
            }
        } catch (err) {
            console.error("Update error:", err);
        }
    };

    // click slidebar scroll tới hình vẽ
    useEffect(() => {
        if (
            typeof scrollIndex === "number" &&
            scrollIndex >= 0 &&
            scrollIndex < paths.length
        ) {
            const el = document.getElementById(`drawing-${scrollIndex}`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            setHighlightIndex(scrollIndex);
            const highlightTimer = setTimeout(() => setHighlightIndex(null), 1500);

            const timer = setTimeout(() => setScrollIndex(null), 300);
            return () => clearTimeout(timer);
        }
    }, [scrollIndex, paths.length, setScrollIndex]);

    // vẽ lại các hình đã vẽ
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Vẽ các hình đã lưu
        paths.forEach((r) => {
            ctx.fillStyle = "rgba(255,0,0,0.2)";
            ctx.strokeStyle = "red";
            ctx.fillRect(r.x, r.y, r.w, r.h);
            ctx.strokeRect(r.x, r.y, r.w, r.h);
        });

        // Vẽ hình đang vẽ
        if (currentRect) {
            ctx.fillStyle = "rgba(0,0,255,0.2)";
            ctx.strokeStyle = "blue";
            ctx.fillRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
            ctx.strokeRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
        }
    }, [paths, currentRect]);

    return (
        <>
            <div
                className={`card ${dragOver ? "drag-over" : ""}`}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onPaste={(e: React.ClipboardEvent<HTMLDivElement>) => {
                    const items = e.clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        if (item.kind === "file") {
                            const file = item.getAsFile();
                            if (file) handleFileUpload(file);
                        }
                    }
                }}
            >
                <div className="card__list" ref={containerRef} style={{ position: "relative" }}>
                    {data?.uploaded_images?.map((img: any, index: number) => (
                        <div id={`detail-${index}`} className="card__list__item" key={img.id} ref={index === data.uploaded_images.length - 1 ? lastImageRef : null}>
                            <img
                                src={`${API_FILE}/${img.image_path}`}
                                alt={img.image_name || "uploaded image"}
                            />
                        </div>

                    ))}
                    <canvas
                        ref={canvasRef}
                        style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                    />
                    {paths.map((r, i) => (
                        <div
                            key={i}
                            id={`drawing-${i}`}
                            style={{
                                position: "absolute",
                                top: r.y,
                                left: r.x,
                                width: r.w,
                                height: r.h,
                                pointerEvents: "none",
                                border: isDetailPage ? "2px solid red" : "none",
                                backgroundColor: isDetailPage ? "rgba(255,0,0,0.2)" : "transparent",
                            }}
                            className={`relative ${highlightIndex === i ? "!bg-red-700" : ""} ${isDetailPage ? "drawn_detail" : ""}`}
                        >
                            {/* Số thứ tự hiển thị bằng div */}
                            <div
                                style={{
                                    position: "absolute",
                                    top: -16, // căn phía trên hoặc tùy chỉnh
                                    right: -16, // căn phía trên bên phải
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    backgroundColor: "red",
                                    color: "#fff",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    fontSize: 12,
                                    fontWeight: "bold",
                                    pointerEvents: "none",
                                }}
                            >
                                {i + 1}
                            </div>
                        </div>
                    ))}

                    {activeRectIndex !== null && !currentRect && saveButtonPos && (
                        <div
                            className="drawing-ctl"
                            style={{
                                position: "absolute",
                                top: saveButtonPos.top,
                                left: saveButtonPos.left,
                                zIndex: 10,
                            }}
                        >
                            <h4>Nhập ghi chú</h4>
                            <textarea
                                name="note"
                                className="border px-4 py-2 rounded-lg"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            ></textarea>

                            {paths[activeRectIndex]?.detailId ? (
                                <button onClick={updateDrawing} className="bg-blue-500 text-white px-4 py-2 rounded">
                                    Sửa
                                </button>
                            ) : (
                                <button onClick={saveDrawing} className="bg-green-500 text-white px-4 py-2 rounded">
                                    Lưu
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {!isDetailPage && (
                    <div className="card__add">
                        <div className="add" onClick={handleUploadClick}>
                            <i className="fa-solid fa-circle-plus fa-2x"></i>
                            <p>Upload</p>
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        <div className="capture">
                            <i className="fa fa-camera-retro fa-2x"></i>
                            <p>Capture url</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}